'use client';

import { MIND_MODE, WS_MIND_TYPE } from '@mindverse/accessor-open';
import classNames from 'classnames';
import { useSearchParams } from 'next/navigation';
import { forwardRef, memo, useCallback, useContext, useEffect, useRef, useState } from 'react';

import App, { EAppMode } from '@/components/chat-app';
import type { EventCreateSessionInterface } from '@/components/chat-app/utils/utils';
import { CONTAINER_EVENT } from '@/components/chat-app/utils/utils';
import HomeContext from '@/context/HomeContext';
import { useChatManager } from '@/hooks/useChatManager';
import useGenius from '@/hooks/useGenius';
import useGeniusLayout from '@/hooks/useGeniusLayout';
import { useModel } from '@/hooks/useModel';
import { getAgentSession, getRecommendedMessage } from '@/service/note';
import { EVENT } from '@/utils/event';

import CallChatPage from '../CallChatPage';
import CircleChat from '../CircleChat';
import HomePage from '../HomePage';
import TopBar from './TopBar';
// import Tutorial from '../tutorial';
import styles from './index.less';

interface IProps {
  inMobileDevice?: boolean;
  className?: string;
}

// eslint-disable-next-line react/display-name
const PersonalAIPage = forwardRef<HTMLDivElement, IProps>((props, ref) => {
  const { inMobileDevice, className } = props;
  const detailInfo = useGenius();

  const { voiceMute, chatHomepageRoute, chatCircleRoute, chatCallContext } =
    useContext(HomeContext);
  const { shadowMindId: mindId } = detailInfo;
  const sessionRef = useRef<string>('');
  const { sessionSocketManager } = useChatManager();
  const { chatStatus } = useGeniusLayout();
  const { userProfile } = useModel('auth-model', (model) => ({ userProfile: model.userProfile }));

  const searchParams = useSearchParams();
  const searchSessionId = searchParams.get('sessionId');

  const [agentSessionId, setAgentSessionId] = useState<string | undefined>(undefined);
  const agentSessionIdRef = useRef<string | undefined>(undefined);
  const hasInitializedRef = useRef<boolean>(false);
  const lastUserActivityTimeRef = useRef<number>(Date.now());
  const recommendTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (searchSessionId) {
      const newSearchParams = new URLSearchParams(searchParams);

      dispatchEvent(new CustomEvent(EVENT.CHANGE_CHAT_STATUS_LAYOUT_TRULY, { detail: 'normal' }));

      newSearchParams.delete('sessionId');
      const newUrl = `${window.location.pathname}${
        newSearchParams.toString() ? `?${newSearchParams}` : ''
      }`;

      history.replaceState(null, '', newUrl);
    }
  }, [searchParams, searchSessionId]);

  const sessionCb = useCallback((_mindId: string, sessionId: string) => {
    sessionRef.current = sessionId;
    dispatchEvent(new CustomEvent(EVENT.SET_CURRENT_SESSION_ID, { detail: sessionId }));
  }, []);

  // 监听用户发送消息事件,重置定时器
  useEffect(() => {
    const handleUserSendMessage = () => {
      if (chatHomepageRoute || chatCircleRoute) {
        return;
      }

      lastUserActivityTimeRef.current = Date.now();

      // 用户发送消息后,重置定时器
      if (recommendTimerRef.current) {
        clearTimeout(recommendTimerRef.current);
      }

      // 重新设置30分钟后的定时器
      if (!chatHomepageRoute && !chatCircleRoute && agentSessionId) {
        recommendTimerRef.current = setTimeout(
          () => {
            getRecommendedMessage();
          },
          30 * 60 * 1000
        );
      }
    };

    window.addEventListener(EVENT.HAS_SEND_MESSAGE, handleUserSendMessage);

    return () => {
      window.removeEventListener(EVENT.HAS_SEND_MESSAGE, handleUserSendMessage);
    };
  }, [chatHomepageRoute, chatCircleRoute, agentSessionId]);

  // 获取 agent session - 只在首次加载时调用
  useEffect(() => {
    // 如果已经初始化过,不再重新获取
    if (hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = true;

    getAgentSession()
      .then((res) => {
        if (res.data.data.sessionId) {
          const sessionId = res.data.data.sessionId;

          setAgentSessionId(sessionId);
          agentSessionIdRef.current = sessionId;
        }
      })
      .catch((err) => {
        console.error('Failed to get agent session:', err);
      });
  }, []);

  // 从 HomePage 或 CircleChat 返回时,确保 agentSessionId 存在
  useEffect(() => {
    // 当返回到 chat 页面时,确保 agentSessionId 已设置
    if (!chatHomepageRoute && !chatCircleRoute && agentSessionIdRef.current && !agentSessionId) {
      setAgentSessionId(agentSessionIdRef.current);
    }
  }, [chatHomepageRoute, chatCircleRoute, agentSessionId]);

  // 每次从 HomePage 或 CircleChat 返回时,重新创建会话连接
  useEffect(() => {
    if (
      !chatHomepageRoute &&
      !chatCircleRoute &&
      agentSessionId &&
      mindId &&
      sessionSocketManager
    ) {
      // 延迟确保 App 组件中的 useChatSession 事件监听器已注册
      const timer = setTimeout(() => {
        dispatchEvent(
          new CustomEvent<EventCreateSessionInterface>(CONTAINER_EVENT.CREATE_SESSION, {
            detail: {
              params: {
                originSessionId: agentSessionId,
                newSession: false
              },
              callback: (sessionId: string) => {
                sessionRef.current = sessionId;
              }
            }
          })
        );
      }, 100);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [chatHomepageRoute, chatCircleRoute, agentSessionId, mindId, sessionSocketManager]);

  // 设置推荐消息定时器:30分钟无活跃后触发
  useEffect(() => {
    const startRecommendTimer = () => {
      // 清除旧定时器
      if (recommendTimerRef.current) {
        clearTimeout(recommendTimerRef.current);
      }

      // 设置30分钟后触发推荐消息
      recommendTimerRef.current = setTimeout(
        () => {
          if (!chatHomepageRoute && !chatCircleRoute && agentSessionId) {
            getRecommendedMessage()
              .then((res) => {
                if (res.data.data.sessionId) {
                  // 推荐消息会通过 WebSocket 推送,这里只是触发
                }
              })
              .catch((err) => {
                console.error('Failed to get recommended message:', err);
              });
          }
        },
        30 * 60 * 1000
      ); // 30分钟
    };

    // 页面加载时启动定时器
    if (!chatHomepageRoute && !chatCircleRoute && agentSessionId) {
      startRecommendTimer();
    }

    // 清理函数
    return () => {
      if (recommendTimerRef.current) {
        clearTimeout(recommendTimerRef.current);
        recommendTimerRef.current = null;
      }
    };
  }, [chatHomepageRoute, chatCircleRoute, agentSessionId]);

  useEffect(() => {
    function getSessionId(event: Event): void {
      const callBack = (event as CustomEvent).detail;

      if (mindId && callBack) {
        callBack(sessionRef.current);
      }
    }

    window.addEventListener(EVENT.GET_SESSION_ID, getSessionId);

    return () => {
      window.removeEventListener(EVENT.GET_SESSION_ID, getSessionId);
    };
  }, [mindId]);

  if (!mindId) {
    return <div ref={ref} />;
  }

  return (
    <div
      ref={ref}
      className={classNames(styles.personalAIPage, className, {
        [styles.friendHomepage]: !!chatHomepageRoute,
        [styles.circleChat]: !!chatCircleRoute,
        [styles.callChat]: !!chatCallContext,
        [styles.inMobileDevice]: inMobileDevice
      })}
    >
      {chatHomepageRoute ? (
        <HomePage
          key={chatHomepageRoute}
          className={!inMobileDevice ? 'rounded-[28px]' : ''}
          forceInMobile={inMobileDevice || chatStatus !== 'fullScreen'}
          origin="chat"
          route={chatHomepageRoute}
        />
      ) : null}
      {chatCircleRoute ? (
        <CircleChat
          key={chatCircleRoute}
          className={!inMobileDevice ? 'rounded-[28px]' : ''}
          forceInMobile={inMobileDevice || chatStatus !== 'fullScreen'}
          route={chatCircleRoute}
        />
      ) : null}
      {chatCallContext ? (
        <CallChatPage
          key={chatCallContext.sessionId}
          className={!inMobileDevice ? 'rounded-[28px]' : ''}
          context={chatCallContext}
          forceInMobile={inMobileDevice || chatStatus !== 'fullScreen'}
        />
      ) : null}
      {/* 只有在没有打开 homepage 或 circle 时才渲染 chatApp,避免多个 App 实例同时监听事件 */}
      {!chatHomepageRoute && !chatCircleRoute && agentSessionId ? (
        <div
          className={classNames(styles.chatAppWrapper, {
            [styles.inMobileDevice]: inMobileDevice
          })}
        >
          {/* 背景图/视频 - 优先展示视频 */}
          {(userProfile?.homepage?.video || userProfile?.homepage?.cover) && (
            <div className={styles.chatAppBackground}>
              {userProfile.homepage.video ? (
                <video
                  autoPlay
                  className={styles.chatAppBackgroundImg}
                  crossOrigin="anonymous"
                  loop
                  muted
                  playsInline
                  src={userProfile.homepage.video}
                />
              ) : (
                <img
                  alt=""
                  className={styles.chatAppBackgroundImg}
                  crossOrigin="anonymous"
                  src={userProfile.homepage.cover}
                />
              )}
            </div>
          )}
          {/* TopBar 在盒子内部 */}
          {!inMobileDevice && <TopBar className="!z-10" />}
          <div className={styles.chatApp}>
            <App
              chatContainer={{
                disabled: false,
                autoCreateSession: false,
                appMode:
                  inMobileDevice || chatStatus !== 'fullScreen'
                    ? EAppMode.CHAT_MOBILE
                    : EAppMode.CHAT_MODE,
                config: {
                  mindConfig: {
                    mindId: mindId,
                    mindType: WS_MIND_TYPE.shadow,
                    mode: MIND_MODE.PERSONAL // pa的mode需要是personal
                  },
                  userId: userProfile?.userId,
                  sessionManager: sessionSocketManager,
                  voiceMute: voiceMute
                },
                sessionCb
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
});

export default memo(PersonalAIPage);
